import type { MapDocument } from "./types";
import type { MapComparison, MapEntityComparison } from "./history";

function canonical(value: unknown): string {
  if (value === undefined) return "undefined";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  return `{${Object.entries(value)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`)
    .join(",")}}`;
}
function entities(
  before: { id: string; label: string; value: object }[],
  after: { id: string; label: string; value: object }[],
): MapEntityComparison {
  const old = new Map(before.map((item) => [item.id, item]));
  const next = new Map(after.map((item) => [item.id, item]));
  const result: MapEntityComparison = {
    added: 0,
    removed: 0,
    changed: 0,
    items: [],
    truncated: false,
  };
  for (const id of new Set([...old.keys(), ...next.keys()])) {
    const a = old.get(id),
      b = next.get(id);
    const change = !a
      ? "added"
      : !b
        ? "removed"
        : canonical(a.value) !== canonical(b.value)
          ? "changed"
          : null;
    if (!change) continue;
    result[change]++;
    const av = a?.value ?? {},
      bv = b?.value ?? {};
    const fields =
      change === "changed"
        ? [...new Set([...Object.keys(av), ...Object.keys(bv)])].filter(
            (key) =>
              canonical(Reflect.get(av, key)) !==
              canonical(Reflect.get(bv, key)),
          )
        : [];
    if (result.items.length < 100)
      result.items.push({ id, label: (b ?? a)!.label, change, fields });
    else result.truncated = true;
  }
  return result;
}
/** Shared by server review endpoints and recovered-draft comparisons. */
export function compareMapDocuments(
  before: MapDocument | null,
  after: MapDocument,
): MapComparison {
  const booths = (document: MapDocument | null) =>
    document?.geometry.stands.map((stand) => ({
      id: stand.id,
      label: `${stand.id} · ${stand.name}`,
      value: { ...stand, displayLabel: document.labels.stands[stand.id] },
    })) ?? [];
  const locations = (document: MapDocument | null) =>
    document?.locations.map((location) => ({
      id: location.id,
      label: location.label,
      value: {
        ...location,
        entrance: document.geometry.entrances.find(
          (entrance) => entrance.id === location.id,
        ),
        displayLabel: document.labels.entrances[location.id],
      },
    })) ?? [];
  const settings = (document: MapDocument | null) =>
    document
      ? {
          title: document.title,
          description: document.description,
          camera: document.camera,
          ...Object.fromEntries(
            Object.entries(document.geometry).filter(
              ([key]) => key !== "stands" && key !== "entrances",
            ),
          ),
          entranceMarkers: document.geometry.entrances,
          labels: document.labels,
        }
      : {};
  const previous = settings(before),
    current = settings(after);
  const changedSettings = [
    ...new Set([...Object.keys(previous), ...Object.keys(current)]),
  ].filter(
    (key) =>
      canonical(Reflect.get(previous, key)) !==
      canonical(Reflect.get(current, key)),
  );
  const boothChanges = entities(booths(before), booths(after));
  const locationChanges = entities(locations(before), locations(after));
  return {
    hasChanges: Boolean(
      changedSettings.length ||
      boothChanges.added ||
      boothChanges.removed ||
      boothChanges.changed ||
      locationChanges.added ||
      locationChanges.removed ||
      locationChanges.changed,
    ),
    booths: boothChanges,
    locations: locationChanges,
    settings: changedSettings,
  };
}
